/**
 * Profile Purchases Route Definition (Non-Lazy)
 *
 * Loader only — the list UI (radix Select, fixtures, market styles) lives in
 * index.lazy.tsx so its imports stay out of the entry chunk.
 */
import { createFileRoute } from '@tanstack/react-router';
import { PurchasesListTemplatePending } from '@/components/v2/page-template-pending';
import { authenticatedGetEnvelopeShared } from '@/lib/api/authenticated-shared';
import { cacheFirst } from '@/lib/queries/cache-first';
import {
  continuePurchasesWalk,
  fetchPurchasesFirstPage,
  fetchPurchasesServer,
} from '@/lib/server-fns/market-purchases-fns';
import type { PurchaseListEnvelope } from '@/lib/api/v2/market';

export const Route = createFileRoute('/_authenticated/profile/purchases/')({
  // Template-first pending: while this route's component chunk downloads, show the
  // destination's page shape instead of the full-page RoutePending mascot
  // (audit part 2: a detail click must switch immediately, skeletons included).
  // The list is title + filter toolbar + quick tabs + wide purchase cards, not
  // generic 84px rows.
  pendingComponent: PurchasesListTemplatePending,
  loader: ({ context }) => {
    const refresh = () =>
      context.queryClient.ensureQueryData({
        queryKey: ['market', 'purchases'],
        queryFn: () => {
          // SSR keeps the full blocking walk: the server-internal hops are
          // cheap and the dehydrator must snapshot the complete set before the
          // response flushes. Client navigations block on the FIRST page only
          // (up to 100 rows — the server max, and the entire history for most
          // users); pages beyond it continue in the background and merge into
          // this same cache entry, so the list paints after one round trip
          // instead of N sequential ones. The final set is identical to the
          // SSR primer's, so a client-side background refetch (the mounted
          // observer's marketApi.listPurchases walk) still produces the same
          // array.
          if (typeof window === 'undefined') return fetchPurchasesServer();
          const fetchPage = (path: string) =>
            authenticatedGetEnvelopeShared<PurchaseListEnvelope>(path);
          return fetchPurchasesFirstPage(fetchPage).then((first) => {
            if (first.nextCursor) {
              void continuePurchasesWalk(
                context.queryClient,
                ['market', 'purchases'],
                first,
                fetchPage
              ).catch(() => undefined);
            }
            return first.rows;
          });
        },
      });
    // Cache-first gate: re-entry (e.g. back from a purchase detail or
    // checkout) resolves from the cached list instantly while the background
    // refresh heals staleness; a cold cache keeps the streamed fetch the
    // Await boundary renders against.
    const { cached, stream } = cacheFirst(context.queryClient, ['market', 'purchases'], refresh);
    return { purchasesPromise: cached !== undefined ? Promise.resolve(cached) : stream() };
  },
});
